import { apiClient } from '@/lib/api/client';
import type {
  MerchantReceiptCorrectInput,
  MerchantReceiptCorrectResponse,
  MerchantReceiptDetail,
  MerchantReceiptsQueryParams,
  MerchantReceiptsResponse,
} from './types';

export async function getMerchantReceipts(
  params: MerchantReceiptsQueryParams,
): Promise<MerchantReceiptsResponse> {
  const { data } = await apiClient.get<MerchantReceiptsResponse>('/merchant/receipts', { params });
  return data;
}

export async function getMerchantReceiptDetail(transactionId: string): Promise<MerchantReceiptDetail> {
  const { data } = await apiClient.get<MerchantReceiptDetail>(
    `/merchant/receipts/${transactionId}`,
  );
  return data;
}

export async function correctMerchantReceipt(
  transactionId: string,
  payload: MerchantReceiptCorrectInput,
): Promise<MerchantReceiptCorrectResponse> {
  const { data } = await apiClient.post<MerchantReceiptCorrectResponse>(
    `/merchant/receipts/${transactionId}/correct`,
    payload,
  );
  return data;
}

/**
 * Download the merchant's receipts as a streaming CSV file (owner-only).
 *
 * The endpoint returns `text/csv`, so we request the response as a Blob rather
 * than letting axios parse it as JSON. The `/merchant/receipts/export.csv` path
 * is NOT in the client interceptor's branch-scoping allowlist (that set matches
 * the exact `/merchant/receipts` path), so `branch_id` is passed explicitly here
 * when a single branch is active — never double-appended.
 */
export async function exportMerchantReceiptsCsv(branchId?: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>('/merchant/receipts/export.csv', {
    responseType: 'blob',
    params: branchId ? { branch_id: branchId } : undefined,
  });
  return data;
}

/**
 * The receipt as it printed, rendered by the backend from the device's raw
 * ESC/POS. Returned as a blob because the endpoint serves `image/png` and the
 * bearer token has to ride on the request — a bare `<img src>` cannot carry it.
 */
export async function getMerchantReceiptOriginalPng(transactionId: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(
    `/merchant/receipts/${encodeURIComponent(transactionId)}/original.png`,
    { responseType: 'blob' },
  );

  return data;
}
